import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { WorkflowPipeline, SearchBar, EmptyState, SectionCard } from "@/components/shared";
import {
  AdminDashboard, DocCategoriesTab, SettingsTab,
  CloneRoleDialog,
} from "@/pages/admin/AdminSections";
import { WorkAssignmentTab } from "@/pages/admin/WorkAssignment";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700",
  inactive: "bg-slate-100 text-slate-500",
  suspended: "bg-red-50 text-red-700",
  pending_verification: "bg-amber-50 text-amber-700",
};
const STATUS_LABELS = {
  active: "Active", inactive: "Inactive", suspended: "Suspended", pending_verification: "Pending Verification",
};

// Access status — derived server-side from status + email_verified + last_login_at
const ACCESS_STATUS_CFG = {
  access_ready:    { label: "Access Ready",         cls: "bg-emerald-50 text-emerald-700",  icon: "ShieldCheck" },
  setup_required:  { label: "Setup Required",        cls: "bg-amber-50 text-amber-700",      icon: "KeyRound" },
  invite_pending:  { label: "Invite Pending",        cls: "bg-blue-50 text-blue-700",        icon: "MailCheck" },
  disabled:        { label: "Disabled",              cls: "bg-slate-100 text-slate-500",     icon: "Ban" },
  suspended:       { label: "Suspended",             cls: "bg-red-50 text-red-700",          icon: "Ban" },
};

const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

function StatusBadge({ status }) {
  return <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLES[status] || STATUS_STYLES.inactive}`}>{STATUS_LABELS[status] || status}</span>;
}

function AccessStatusBadge({ accessStatus }) {
  const cfg = ACCESS_STATUS_CFG[accessStatus] || { label: accessStatus, cls: "bg-slate-100 text-slate-500", icon: "Circle" };
  const Icon = Icons[cfg.icon] || Icons.Circle;
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`} title={`Access: ${cfg.label}`}>
      <Icon className="h-3 w-3" />{cfg.label}
    </span>
  );
}

function AddUserDialog({ roles, teams, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "read_only", team: "" });
  const [saving, setSaving] = useState(false);
  const [recoverable, setRecoverable] = useState(null); // {existing_user_id, existing_name, access_status, suggested_action, ...}
  const [restoreForm, setRestoreForm] = useState({ role: "read_only", team: "" });

  // Pre-populate restore form from the deleted-account 409 detail
  useEffect(() => {
    if (recoverable?.access_status === "account_deleted") {
      setRestoreForm({
        role: recoverable.existing_role || "read_only",
        team: recoverable.existing_team || "",
      });
    }
  }, [recoverable]);

  const submit = async () => {
    setRecoverable(null);
    // Client-side validation before hitting the server
    if (!form.name.trim()) { toast.error("Full name is required"); return; }
    if (!form.email.trim() || !form.email.includes("@")) { toast.error("A valid email address is required"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (!form.role) { toast.error("A role must be selected"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/users", { ...form, team: form.team || null });
      const emailMsg = data?.email_accepted === true
        ? "Setup email sent successfully."
        : data?.email_accepted === false
          ? (data.email_message || "Setup email could not be delivered. Use 'Resend Account Setup' from the user list when the email service is available.")
          : (data?.setup_email_queued ? "Setup email queued." : "Account created.");
      if (data?.email_accepted === false) {
        toast.success("User created.");
        toast.warning(emailMsg, { duration: 8000 });
      } else {
        toast.success(`User created. ${emailMsg}`);
      }
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "read_only", team: "" });
      onCreated();
    } catch (e) {
      const detail = e.response?.data?.detail;
      // Any structured 409 detail goes to the recovery/restore panel
      const isStructured409 = e.response?.status === 409 && detail && typeof detail === "object";
      if (isStructured409) {
        setRecoverable(detail);
      } else if (detail) {
        toast.error(formatApiErrorDetail(detail));
      } else if (e.response?.status === 422) {
        toast.error("Form validation failed. Check all fields and try again.");
      } else if (!e.response) {
        toast.error("Connection error. The server may be busy — please try again in a moment.");
      } else {
        toast.error(`Server error (${e.response.status}). Please try again or contact support.`);
      }
    } finally { setSaving(false); }
  };

  const sendSetup = async () => {
    if (!recoverable?.existing_user_id) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${recoverable.existing_user_id}/resend-account-setup`);
      toast.success(data.message || "Account setup email sent");
      setOpen(false);
      setRecoverable(null);
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send setup email");
    } finally { setSaving(false); }
  };

  const restoreAccount = async () => {
    if (!recoverable?.existing_user_id) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${recoverable.existing_user_id}/restore`, {
        role: restoreForm.role,
        team: restoreForm.team || null,
        name: recoverable.existing_name || undefined,
      });
      if (data?.email_accepted === false) {
        toast.success(data.message || "Account restored.");
        toast.warning(data.email_message || "Setup email could not be delivered. Use 'Resend Account Setup' from the user list.", { duration: 6000 });
      } else {
        toast.success(data.message || (data?.email_accepted ? "Account restored. Setup email sent." : "Account restored."));
      }
      setOpen(false);
      setRecoverable(null);
      setForm({ name: "", email: "", password: "", role: "read_only", team: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    } finally { setSaving(false); }
  };

  const handleClose = (v) => {
    setOpen(v);
    if (!v) { setRecoverable(null); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <button data-testid="add-user-button" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium">
          <Icons.UserPlus className="h-4 w-4" /> Add User
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="add-user-dialog">
        <DialogHeader><DialogTitle className="font-heading">Add Team Member</DialogTitle></DialogHeader>

        {recoverable?.access_status === "account_deleted" ? (
          /* ── Restore Deleted Account panel ── */
          <div className="space-y-4" data-testid="restore-account-panel">
            <div className="border rounded-lg p-4 bg-amber-50 border-amber-200 text-sm">
              <div className="flex items-start gap-2 text-amber-900">
                <Icons.RotateCcw className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
                <div>
                  <p className="font-semibold">Restore Deleted Account</p>
                  <p className="mt-0.5 text-amber-800">
                    This email belonged to a staff account that was previously deleted. Restoring it will re-enable the
                    original identity and send a new account setup email — no duplicate record will be created.
                  </p>
                </div>
              </div>
            </div>

            {/* Existing identity summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 text-sm space-y-0.5">
              <p className="font-medium text-slate-800" data-testid="restore-existing-name">
                {recoverable.existing_name || "(no name recorded)"}
              </p>
              <p className="text-slate-500">{form.email}</p>
              {recoverable.existing_title && (
                <p className="text-slate-400 text-xs">{recoverable.existing_title}</p>
              )}
            </div>

            {/* Role / team review */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Role</label>
                <Select value={restoreForm.role} onValueChange={(v) => setRestoreForm({ ...restoreForm, role: v })}>
                  <SelectTrigger data-testid="restore-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Team</label>
                <Select value={restoreForm.team} onValueChange={(v) => setRestoreForm({ ...restoreForm, team: v })}>
                  <SelectTrigger data-testid="restore-team"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>{teams.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => setRecoverable(null)}
                className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50"
                data-testid="restore-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={restoreAccount}
                disabled={saving || !restoreForm.role}
                data-testid="restore-account-btn"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium"
              >
                {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.RotateCcw className="h-4 w-4" />}
                Restore Account
              </button>
            </div>
          </div>
        ) : recoverable ? (
          /* ── Active / Setup-required recovery panel ── */
          <div className="space-y-4" data-testid="recovery-panel">
            <div className={`border rounded-lg p-4 text-sm bg-amber-50 border-amber-200 text-amber-800`}>
              <div className="flex items-start gap-2">
                <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Email already has a staff account</p>
                  <p className="mt-1">
                    <strong>{recoverable.existing_name || form.email}</strong> already exists with access status:{" "}
                    <strong>{ACCESS_STATUS_CFG[recoverable.access_status]?.label || recoverable.access_status}</strong>.
                    A new user was not created. Send a recovery link to the existing account instead.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setRecoverable(null)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50">Back</button>
              <button onClick={sendSetup} disabled={saving} data-testid="send-account-setup-btn" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium">
                {saving ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Mail className="h-4 w-4" />}
                Send Account Setup
              </button>
            </div>
          </div>
        ) : (
          /* ── Create new user form ── */
          <>
            <div className="space-y-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Full name</label>
                <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1 block">Temporary password</label>
                <input type="text" className={inputCls} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" placeholder="min 8 characters" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Role</label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                    <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><label className="text-sm font-medium text-slate-700 mb-1 block">Team</label>
                  <Select value={form.team} onValueChange={(v) => setForm({ ...form, team: v })}>
                    <SelectTrigger data-testid="new-user-team"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            </div>
            <DialogFooter>
              <button onClick={submit} disabled={saving} data-testid="new-user-submit" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium">
                {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} Create User
              </button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, roles, teams, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ role: user.role, team: user.team || "", status: user.status, mfa_required: user.mfa_required });
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm({ role: user.role, team: user.team || "", status: user.status, mfa_required: user.mfa_required }); }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { role: form.role, team: form.team || null, status: form.status });
      if (form.mfa_required !== user.mfa_required) {
        await api.post(`/users/${user.id}/mfa`, { mfa_required: form.mfa_required });
      }
      toast.success("User updated");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
        <DialogHeader><DialogTitle className="font-heading">Edit {user.name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1 block">Team</label>
              <Select value={form.team} onValueChange={(v) => setForm({ ...form, team: v })}>
                <SelectTrigger data-testid="edit-user-team"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>{teams.map((t) => <SelectItem key={t.key} value={t.key}>{t.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div><label className="text-sm font-medium text-slate-700 mb-1 block">Status</label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="edit-user-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5">
            <div>
              <div className="text-sm font-medium text-slate-700">Require MFA</div>
              <div className="text-xs text-slate-400">Enforce multi-factor at next login (MFA-ready)</div>
            </div>
            <Switch checked={form.mfa_required} onCheckedChange={(v) => setForm({ ...form, mfa_required: v })} data-testid="edit-user-mfa" />
          </div>
        </div>
        <DialogFooter>
          <button onClick={save} disabled={saving} data-testid="edit-user-save" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} Save Changes
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LoginHistoryDialog({ user, open, onOpenChange }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (open && user) api.get(`/users/${user.id}/login-history`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [open, user]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="login-history-dialog">
        <DialogHeader><DialogTitle className="font-heading">Login History — {user?.name}</DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-y-auto tf-scroll">
          {rows.length === 0 ? <p className="text-sm text-slate-500 py-6 text-center">No login history.</p> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2 font-semibold">Outcome</th><th className="py-2 font-semibold">IP</th><th className="py-2 font-semibold">When</th></tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-50">
                    <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full ${r.outcome === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{r.outcome}</span></td>
                    <td className="py-2 text-slate-500">{r.ip}</td>
                    <td className="py-2 text-slate-500">{new Date(r.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PermissionMatrix({ roles, reload }) {
  const [meta, setMeta] = useState({ modules: [], actions: [], action_labels: {} });
  const [selected, setSelected] = useState(null);
  const [keys, setKeys] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/rbac/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (roles.length && !selected) setSelected(roles.find((r) => r.key === "sales") || roles[0]);
  }, [roles, selected]);
  useEffect(() => {
    if (selected) setKeys(new Set(selected.permission_keys || []));
  }, [selected]);

  const wildcard = keys.has("*");
  const toggle = (m, a) => {
    const k = `${m}.${a}`;
    const next = new Set(keys);
    next.has(k) ? next.delete(k) : next.add(k);
    setKeys(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const permission_keys = Array.from(keys).sort();
      const modules = meta.modules.filter((m) => keys.has(`${m}.view`));
      await api.patch(`/roles/${selected.key}`, { permission_keys, modules });
      toast.success(`Permissions updated for ${selected.label}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="permission-matrix">
      <div className="lg:col-span-1 bg-white border border-slate-200 rounded-xl shadow-sm p-2 h-fit">
        {roles.map((r) => (
          <button key={r.key} onClick={() => setSelected(r)} data-testid={`role-select-${r.key}`}
            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${selected?.key === r.key ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-600 hover:bg-slate-50"}`}>
            {r.label}
          </button>
        ))}
      </div>
      <div className="lg:col-span-3 bg-white border border-slate-200 rounded-xl shadow-sm p-5">
        {!selected ? <p className="text-slate-500 text-sm">Select a role.</p> : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-heading font-medium text-slate-800">{selected.label}</h3>
                <p className="text-sm text-slate-500">{selected.description}</p>
              </div>
              {!wildcard && (
                <button onClick={save} disabled={saving} data-testid="save-permissions" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium">
                  {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} Save Permissions
                </button>
              )}
            </div>
            {wildcard ? (
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-800 flex items-center gap-2">
                <Icons.ShieldCheck className="h-5 w-5" /> Full access (wildcard). This role can perform every action on every module.
              </div>
            ) : (
              <div className="overflow-x-auto tf-scroll">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-slate-100">
                      <th className="py-2 pr-4 font-semibold text-slate-500 text-xs uppercase tracking-wider sticky left-0 bg-white">Module</th>
                      {meta.actions.map((a) => (
                        <th key={a} className="py-2 px-1 font-medium text-slate-500 text-[11px] text-center whitespace-nowrap">{meta.action_labels[a]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {meta.modules.map((m) => (
                      <tr key={m} className="border-b border-slate-50">
                        <td className="py-2 pr-4 text-slate-700 capitalize sticky left-0 bg-white">{m.replace(/_/g, " ")}</td>
                        {meta.actions.map((a) => (
                          <td key={a} className="py-2 px-1 text-center">
                            <Checkbox checked={keys.has(`${m}.${a}`)} onCheckedChange={() => toggle(m, a)} data-testid={`perm-${m}-${a}`} />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function Administration() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [teams, setTeams] = useState([]);
  const [history, setHistory] = useState([]);
  const [pipelinesList, setPipelinesList] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditQuery, setAuditQuery] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [historyUser, setHistoryUser] = useState(null);
  const [notifUser, setNotifUser] = useState(null);
  const canManage = ["administrator", "partner"].includes(user?.role);

  const load = async () => {
    try {
      const [u, r, t] = await Promise.all([api.get("/users"), api.get("/roles"), api.get("/teams")]);
      setUsers(u.data || []); setRoles(r.data || []); setTeams(t.data || []);
    } catch (e) { /* ignore */ }
  };
  const loadHistory = async () => {
    try { const { data } = await api.get("/login-history", { params: { limit: 100 } }); setHistory(data || []); } catch (e) {}
  };
  const loadPipelines = async () => {
    try { const { data } = await api.get("/pipelines"); setPipelinesList(data || []); } catch (e) {}
  };
  const loadAudit = async () => {
    try { const { data } = await api.get("/audit-logs", { params: { limit: 100, q: auditQuery || undefined } }); setAuditLogs(data || []); } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  const sendReset = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/send-password-reset`);
      toast.success(data.message || `Password reset email sent to ${u.email}`);
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send password reset"); }
  };

  const sendAccountSetup = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/resend-account-setup`);
      toast.success(data.message || `Account setup email sent to ${u.email}`);
      load();
    }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send account setup email"); }
  };

  const reactivateUser = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { status: "active" });
      toast.success(`${u.name} reactivated`);
      load();
    }
    catch (e) { toast.error("Failed to reactivate"); }
  };
  const removeUser = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div data-testid="administration-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Administration</h1>
        <p className="text-slate-500 mt-1">Operational command center — configure users, access, services and settings.</p>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="mb-4 flex-wrap h-auto gap-x-1 gap-y-2 items-center bg-transparent p-0">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 pl-1 pr-1.5 select-none" data-testid="admin-navgroup-overview">Overview</span>
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>

          <span className="w-px h-5 bg-slate-200 mx-1.5" aria-hidden="true" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 pr-1.5 select-none" data-testid="admin-navgroup-access">Access</span>
          <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">Roles &amp; Permissions</TabsTrigger>
          <TabsTrigger value="teams" data-testid="tab-teams">Teams &amp; Assignment</TabsTrigger>

          <span className="w-px h-5 bg-slate-200 mx-1.5" aria-hidden="true" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 pr-1.5 select-none" data-testid="admin-navgroup-configuration">Configuration</span>
          <TabsTrigger value="categories" data-testid="tab-categories">Doc Categories</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="workflows" data-testid="tab-workflows" onClick={loadPipelines}>Workflows</TabsTrigger>

          <span className="w-px h-5 bg-slate-200 mx-1.5" aria-hidden="true" />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 pr-1.5 select-none" data-testid="admin-navgroup-activity">Activity</span>
          <TabsTrigger value="history" data-testid="tab-history" onClick={loadHistory}>Login History</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit" onClick={loadAudit}>Audit Log</TabsTrigger>
        </TabsList>

        {/* DASHBOARD */}
        <TabsContent value="dashboard"><AdminDashboard /></TabsContent>

        {/* USERS */}
        <TabsContent value="users">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-500">
              <span className="font-medium text-slate-700">{users.length}</span> team members ·{" "}
              <span className="text-emerald-700 font-medium">{users.filter(u => u.access_status === "access_ready").length} access ready</span>
              {users.filter(u => u.access_status === "setup_required").length > 0 && (
                <span className="text-amber-600 font-medium"> · {users.filter(u => u.access_status === "setup_required").length} setup required</span>
              )}
            </p>
            {canManage && <AddUserDialog roles={roles} teams={teams} onCreated={load} />}
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Name</th>
                  <th className="px-5 py-3 font-semibold hidden sm:table-cell">Email</th>
                  <th className="px-5 py-3 font-semibold">Role</th>
                  <th className="px-5 py-3 font-semibold">Record Status</th>
                  <th className="px-5 py-3 font-semibold">Access</th>
                  <th className="px-5 py-3 font-semibold hidden md:table-cell">MFA</th>
                  <th className="px-5 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isDisabledOrSuspended = ["disabled", "suspended"].includes(u.access_status);
                  const isPending = u.access_status === "invite_pending";
                  const needsSetup = u.access_status === "setup_required";
                  return (
                    <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                      <td className="px-5 py-3 text-slate-800 font-medium">{u.name}</td>
                      <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{u.email}</td>
                      <td className="px-5 py-3"><span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">{u.role_label}</span></td>
                      <td className="px-5 py-3"><StatusBadge status={u.status} /></td>
                      <td className="px-5 py-3"><AccessStatusBadge accessStatus={u.access_status || "setup_required"} /></td>
                      <td className="px-5 py-3 hidden md:table-cell">
                        {u.mfa_required ? <span className="text-xs text-amber-700 inline-flex items-center gap-1"><Icons.ShieldAlert className="h-3.5 w-3.5" />Required</span>
                          : <span className="text-xs text-slate-400">Off</span>}
                      </td>
                      <td className="px-5 py-3 text-right">
                        {canManage && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 rounded-lg hover:bg-slate-100 text-slate-600" data-testid={`user-actions-${u.id}`}><Icons.MoreHorizontal className="h-4 w-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={() => setEditUser(u)} data-testid={`user-edit-${u.id}`}>
                                <Icons.Pencil className="h-4 w-4 mr-2 text-slate-500" />Edit user
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {isDisabledOrSuspended ? (
                                <DropdownMenuItem onClick={() => reactivateUser(u)} data-testid={`user-reactivate-${u.id}`} className="text-blue-600 focus:text-blue-600">
                                  <Icons.UserCheck className="h-4 w-4 mr-2" />Reactivate user
                                </DropdownMenuItem>
                              ) : isPending ? (
                                <DropdownMenuItem onClick={() => sendAccountSetup(u)} data-testid={`user-resend-setup-${u.id}`} className="text-blue-600 focus:text-blue-600">
                                  <Icons.MailCheck className="h-4 w-4 mr-2" />Resend account setup
                                </DropdownMenuItem>
                              ) : needsSetup ? (
                                <DropdownMenuItem onClick={() => sendAccountSetup(u)} data-testid={`user-send-setup-${u.id}`} className="text-amber-600 focus:text-amber-600">
                                  <Icons.Mail className="h-4 w-4 mr-2" />Send account setup
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onClick={() => sendReset(u)} data-testid={`user-reset-${u.id}`}>
                                  <Icons.KeyRound className="h-4 w-4 mr-2 text-slate-500" />Send password reset
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => setHistoryUser(u)} data-testid={`user-history-${u.id}`}>
                                <Icons.History className="h-4 w-4 mr-2 text-slate-500" />Login history
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => setNotifUser(u)} data-testid={`user-notif-settings-${u.id}`}>
                                <Icons.Bell className="h-4 w-4 mr-2 text-slate-500" />Notification Settings
                              </DropdownMenuItem>
                              {u.id !== user.id && (<>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => removeUser(u.id)} data-testid={`user-delete-${u.id}`} className="text-red-600 focus:text-red-600"><Icons.Trash2 className="h-4 w-4 mr-2" />Remove user</DropdownMenuItem>
                              </>)}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* ROLES & PERMISSIONS */}
        <TabsContent value="roles">
          {canManage && (
            <div className="flex justify-end mb-3">
              <CloneRoleDialog roles={roles} onSaved={load} />
            </div>
          )}
          <PermissionMatrix roles={roles} reload={load} />
        </TabsContent>

        {/* SERVICES / ADDONS / CATEGORIES / SETTINGS / AUTOMATIONS */}
        {/* Services & Add-ons now live in the Service Library (Administration › Service Library) */}
        <TabsContent value="categories"><DocCategoriesTab canManage={canManage} /></TabsContent>
        <TabsContent value="settings"><SettingsTab canManage={canManage} /></TabsContent>

        {/* TEAMS & ROUTING */}
        <TabsContent value="teams">
          <WorkAssignmentTab canManage={canManage} users={users} />
        </TabsContent>

        {/* WORKFLOWS */}
        <TabsContent value="workflows">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-sm text-slate-500">Read-only workflow definitions showing the standard lifecycle used across the platform.</p>
            <span data-testid="workflows-readonly-badge" title="Workflow definitions are maintained by the platform and shown here for reference." className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
              <Icons.Lock className="h-3 w-3" />Read only
            </span>
          </div>
          <p className="text-xs text-slate-400 mb-4">Live progress is tracked on each lead, client, engagement and production record.</p>
          <div className="space-y-4" data-testid="workflows-list">
            {pipelinesList.length === 0 ? <EmptyState icon="GitMerge" title="No pipelines" /> :
              pipelinesList.map((p) => (
                <SectionCard key={p.key} title={p.name} icon="GitMerge">
                  <WorkflowPipeline stages={p.stages} catalog />
                </SectionCard>
              ))}
          </div>
        </TabsContent>

        {/* LOGIN HISTORY */}
        <TabsContent value="history">          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="login-history-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">Email</th>
                  <th className="px-5 py-3 font-semibold">Outcome</th>
                  <th className="px-5 py-3 font-semibold hidden sm:table-cell">IP</th>
                  <th className="px-5 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {history.length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">Open this tab to load recent login activity.</td></tr>
                ) : history.map((h) => (
                  <tr key={h.id} className="border-b border-slate-50">
                    <td className="px-5 py-3 text-slate-700">{h.email}</td>
                    <td className="px-5 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${h.outcome === "success" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{h.outcome}</span></td>
                    <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{h.ip}</td>
                    <td className="px-5 py-3 text-slate-500">{new Date(h.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* AUDIT LOG */}
        <TabsContent value="audit">
          <div className="flex items-center gap-2 mb-3">
            <SearchBar value={auditQuery} onChange={setAuditQuery} placeholder="Search audit log…" className="w-full sm:w-80" testId="audit-search" />
            <button onClick={loadAudit} data-testid="audit-search-btn" className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2 text-sm font-medium">Search</button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="audit-log-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="px-5 py-3 font-semibold">User</th>
                  <th className="px-5 py-3 font-semibold">Action</th>
                  <th className="px-5 py-3 font-semibold hidden md:table-cell">Summary</th>
                  <th className="px-5 py-3 font-semibold hidden sm:table-cell">IP</th>
                  <th className="px-5 py-3 font-semibold">When</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.length === 0 ? (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">Open this tab to load the audit log.</td></tr>
                ) : auditLogs.map((a) => (
                  <tr key={a.id} className="border-b border-slate-50">
                    <td className="px-5 py-3 text-slate-700">{a.user_name}</td>
                    <td className="px-5 py-3"><span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded font-mono">{a.action}</span></td>
                    <td className="px-5 py-3 text-slate-500 hidden md:table-cell">{a.summary}</td>
                    <td className="px-5 py-3 text-slate-500 hidden sm:table-cell">{a.ip || "—"}</td>
                    <td className="px-5 py-3 text-slate-500">{new Date(a.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {editUser && <EditUserDialog user={editUser} roles={roles} teams={teams} open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)} onSaved={load} />}
      {historyUser && <LoginHistoryDialog user={historyUser} open={!!historyUser} onOpenChange={(o) => !o && setHistoryUser(null)} />}
      {notifUser && <NotifSettingsDialog user={notifUser} open={!!notifUser} onOpenChange={(o) => !o && setNotifUser(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// NotifSettingsDialog — management view/edit of a staff member's notification preferences
// ---------------------------------------------------------------------------
const NOTIF_CATEGORIES_ADMIN = [
  {
    key: "lead_message_email",
    label: "Lead message alerts",
    description: "Email when an assigned lead sends a new inbound message.",
  },
  {
    key: "client_message_email",
    label: "Client message alerts",
    description: "Email when an assigned client sends a new inbound message.",
  },
  {
    key: "conversation_assignment_email",
    label: "Conversation assignment alerts",
    description: "Email when a lead or client conversation is newly assigned to this staff member.",
  },
];

function NotifSettingsDialog({ user: targetUser, open, onOpenChange }) {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!open || !targetUser) return;
    setLoading(true);
    api.get(`/staff-notification-prefs/${targetUser.id}`)
      .then(({ data }) => setPrefs(data.prefs || data))
      .catch(() => { setPrefs(null); })
      .finally(() => setLoading(false));
  }, [open, targetUser]);

  const toggle = async (key) => {
    if (!prefs || saving) return;
    const newVal = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: newVal }));
    setSaving(key);
    try {
      const { data } = await api.put(`/staff-notification-prefs/${targetUser.id}`, { [key]: newVal });
      setPrefs(data.prefs || data);
      toast.success(newVal ? "Email alert enabled" : "Email alert disabled");
    } catch (err) {
      setPrefs((p) => ({ ...p, [key]: !newVal }));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update preference");
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`notif-settings-dialog-${targetUser?.id}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icons.Bell className="h-4 w-4 text-slate-500" />
            Notification Settings — {targetUser?.name}
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <p className="text-sm text-slate-500 mb-4">
            Configure which email alerts this staff member receives.
            Changes are independently audited.
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="notif-settings-loading">
              <Icons.Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : !prefs ? (
            <p className="text-sm text-red-500">Failed to load preferences.</p>
          ) : (
            <div className="space-y-4">
              {NOTIF_CATEGORIES_ADMIN.map(({ key, label, description }) => (
                <div key={key} className="flex items-start justify-between gap-4" data-testid={`admin-notif-row-${key}`}>
                  <div>
                    <div className="text-sm font-medium text-slate-800">{label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{description}</div>
                  </div>
                  <div className="flex-shrink-0 pt-0.5">
                    <Switch
                      checked={!!prefs[key]}
                      onCheckedChange={() => toggle(key)}
                      disabled={saving === key}
                      data-testid={`admin-notif-toggle-${key}-${targetUser?.id}`}
                      aria-label={label}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <button
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg px-4 py-2 text-sm font-medium"
            data-testid="notif-settings-close"
          >
            Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
